import { useState, useCallback } from 'react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAppContext } from '@/hooks/useAppContext';
import { useToast } from '@/hooks/useToast';
import { useNWC } from '@/hooks/useNWCContext';
import { nip57 } from 'nostr-tools';
import type { WebLNProvider } from '@webbtc/webln-types';
import { useNostr } from '@nostrify/react';
import type { NostrEvent } from '@nostrify/nostrify';

interface DirectZapOptions {
  recipientPubkey: string;
  recipientProfile?: NostrEvent | null;
  amountSats: number;
  comment?: string;
}

export type ZapResult =
  | { success: true; zapReceiptId: string }
  | { success: false; reason: 'no-wallet' | 'no-profile' | 'no-ln-address' | 'invoice-failed' | 'payment-failed' | 'receipt-missing' | 'receipt-invalid' | 'unknown'; message: string };

export function useDirectZap(webln: WebLNProvider | null) {
  const { nostr } = useNostr();
  const { toast } = useToast();
  const { user } = useCurrentUser();
  const { config } = useAppContext();
  const { sendPayment, getActiveConnection } = useNWC();

  const [isZapping, setIsZapping] = useState(false);

  /**
   * Attempt to send a NIP-57 profile zap.
   * Requires NWC or WebLN — no manual invoice path.
   * Returns { success: true, zapReceiptId } on confirmed payment,
   * or { success: false } on any failure (no sats move without confirmation).
   */
  const sendZap = useCallback(async ({
    recipientPubkey,
    recipientProfile,
    amountSats,
    comment = '',
  }: DirectZapOptions): Promise<ZapResult> => {
    if (!user || !user.signer) {
      toast({ title: 'Login required', description: 'You must be logged in to send a zap.', variant: 'destructive' });
      return { success: false, reason: 'no-wallet', message: 'Login required' };
    }

    // Block immediately if no wallet is connected — don't even fetch an invoice
    const nwcConn = getActiveConnection();
    const hasWallet = nwcConn?.isConnected || !!webln;
    if (!hasWallet) {
      toast({
        title: 'Wallet required',
        description: 'Connect a Lightning wallet (NWC or browser extension) to pass the torch.',
        variant: 'destructive',
      });
      return { success: false, reason: 'no-wallet', message: 'No Lightning wallet connected' };
    }

    setIsZapping(true);

    try {
      // Fetch recipient profile if not provided
      let profileEvent = recipientProfile ?? null;
      if (!profileEvent) {
        const events = await nostr.query([{ kinds: [0], authors: [recipientPubkey], limit: 1 }]);
        profileEvent = events[0] ?? null;
      }

      if (!profileEvent) {
        toast({ title: 'Profile not found', description: 'Could not load the recipient\'s profile.', variant: 'destructive' });
        return { success: false, reason: 'no-profile', message: 'Recipient profile not found on relays' };
      }

      const zapEndpoint = await nip57.getZapEndpoint(profileEvent);
      if (!zapEndpoint) {
        toast({
          title: 'No Lightning address',
          description: 'This person has no Lightning address on their Nostr profile.',
          variant: 'destructive',
        });
        return { success: false, reason: 'no-ln-address', message: 'Recipient has no Lightning address configured' };
      }

      // Build and sign the zap request (profile zap)
      const zapAmount = amountSats * 1000; // msats
      const zapRequest = nip57.makeZapRequest({
        pubkey: recipientPubkey,
        amount: zapAmount,
        relays: config.relayMetadata.relays.map(r => r.url),
        comment,
      });
      const signedZapRequest = await user.signer.signEvent(zapRequest);

      // Fetch the Lightning invoice from the LNURL endpoint.
      // Some LNURL callbacks already include query params (e.g. webhook_data),
      // so we must use '&' instead of '?' when appending.
      const separator = zapEndpoint.includes('?') ? '&' : '?';
      const invoiceUrl = `${zapEndpoint}${separator}amount=${zapAmount}&nostr=${encodeURIComponent(JSON.stringify(signedZapRequest))}`;
      console.log('[zap] Invoice URL:', invoiceUrl);
      console.log('[zap] Zap request:', JSON.stringify(signedZapRequest, null, 2));

      let res: Response;
      let responseData: Record<string, unknown>;
      try {
        res = await fetch(invoiceUrl);
        const responseText = await res.text();
        try {
          responseData = JSON.parse(responseText);
        } catch {
          responseData = { raw: responseText };
        }
        if (!res.ok) {
          console.error('LNURL invoice error:', { status: res.status, body: responseData });
          const reason = (responseData.reason as string) || (responseData.error as string) || responseText.slice(0, 200) || 'Unknown error';
          throw new Error(`HTTP ${res.status}: ${reason}`);
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Could not fetch Lightning invoice';
        toast({ title: 'Invoice failed', description: msg, variant: 'destructive' });
        return { success: false, reason: 'invoice-failed', message: msg };
      }

      const bolt11 = typeof responseData.pr === 'string' ? responseData.pr : '';
      if (!bolt11) {
        const msg = 'LNURL service did not return a valid invoice';
        toast({ title: 'Invoice failed', description: msg, variant: 'destructive' });
        return { success: false, reason: 'invoice-failed', message: msg };
      }

      // --- Try NWC first ---
      let paymentSucceeded = false;
      if (nwcConn?.isConnected) {
        try {
          await sendPayment(nwcConn, bolt11);
          toast({ title: '⚡ Zap sent!', description: `You sent ${amountSats} sats.` });
          paymentSucceeded = true;
        } catch (nwcErr) {
          console.error('NWC payment failed, trying WebLN:', nwcErr);
          const msg = nwcErr instanceof Error ? nwcErr.message : 'NWC payment failed';
          toast({ title: 'Payment failed', description: `NWC: ${msg}. Trying browser wallet…`, variant: 'destructive' });
          // Fall through to WebLN below
        }
      }

      // --- Try WebLN (primary or fallback) ---
      if (!paymentSucceeded && webln) {
        try {
          let provider = webln;
          if (webln.enable && typeof webln.enable === 'function') {
            const enabled = await webln.enable();
            if (enabled) provider = enabled as WebLNProvider;
          }
          await provider.sendPayment(bolt11);
          toast({ title: '⚡ Zap sent!', description: `You sent ${amountSats} sats.` });
          paymentSucceeded = true;
        } catch (weblnErr) {
          console.error('WebLN payment failed:', weblnErr);
          const msg = weblnErr instanceof Error ? weblnErr.message : 'Browser wallet payment failed';
          toast({ title: 'Payment failed', description: msg, variant: 'destructive' });
          return { success: false, reason: 'payment-failed', message: msg };
        }
      }

      if (!paymentSucceeded) {
        // Should never reach here since we checked hasWallet above,
        // but guard just in case both payment paths were skipped.
        return { success: false, reason: 'payment-failed', message: 'No payment method succeeded' };
      }

      // Payment succeeded. Now wait for the Nostr receipt.
      const receipt = await pollForReceipt(nostr, recipientPubkey, amountSats);

      if (!receipt) {
        const msg = 'Payment confirmed but zap receipt not found on relays. Your sats are safe, but the torch was not passed.';
        toast({ title: 'Receipt not found', description: msg, variant: 'destructive' });
        return { success: false, reason: 'receipt-missing', message: msg };
      }

      // Validate receipt content
      const receiptPTag = receipt.tags.find(([n]) => n === 'p')?.[1];
      const receiptAmountTag = receipt.tags.find(([n]) => n === 'amount')?.[1];
      const receiptAmountSats = receiptAmountTag ? Math.floor(parseInt(receiptAmountTag, 10) / 1000) : null;

      if (receiptPTag !== recipientPubkey) {
        const msg = 'Zap receipt does not match the intended recipient. The payment may have been routed to the wrong person.';
        toast({ title: 'Zap misdirected', description: msg, variant: 'destructive' });
        return { success: false, reason: 'receipt-invalid', message: msg };
      }

      if (receiptAmountSats !== null && receiptAmountSats !== amountSats) {
        const msg = `Zap receipt amount (${receiptAmountSats} sats) does not match the intended amount (${amountSats} sats).`;
        toast({ title: 'Zap amount mismatch', description: msg, variant: 'destructive' });
        return { success: false, reason: 'receipt-invalid', message: msg };
      }

      return { success: true, zapReceiptId: receipt.id };

    } catch (err) {
      console.error('Zap error:', err);
      const msg = err instanceof Error ? err.message : 'Unknown zap error';
      toast({ title: 'Zap failed', description: msg, variant: 'destructive' });
      return { success: false, reason: 'unknown', message: msg };
    } finally {
      setIsZapping(false);
    }
  }, [user, nostr, config, sendPayment, getActiveConnection, webln, toast]);

  return { sendZap, isZapping };
}

/**
 * Poll for a kind 9735 zap receipt by recipient pubkey (#p tag).
 * Profile zaps don't have an #e tag so we filter by #p instead.
 */
async function pollForReceipt(
  nostr: { query: (filters: object[], opts?: object) => Promise<NostrEvent[]> },
  recipientPubkey: string,
  amountSats: number,
  retries = 8,
  delayMs = 5000
): Promise<NostrEvent | null> {
  const since = Math.floor(Date.now() / 1000) - 60;

  for (let attempt = 0; attempt < retries; attempt++) {
    await new Promise(r => setTimeout(r, attempt === 0 ? 3000 : delayMs));

    try {
      const signal = AbortSignal.timeout(8000);
      const events = await nostr.query([{
        kinds: [9735],
        '#p': [recipientPubkey],
        since,
        limit: 10,
      }], { signal });

      // Prefer exact amount match
      for (const event of events) {
        const amountTag = event.tags.find(([n]) => n === 'amount')?.[1];
        if (amountTag && Math.floor(parseInt(amountTag, 10) / 1000) === amountSats) return event;
      }
      // Fall back to any recent receipt for this recipient
      if (events.length > 0) return events[0];
    } catch {
      // timeout or network — keep retrying
    }
  }
  return null;
}
